import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Upload, ChevronDown, Eye, Bell, BellOff } from "lucide-react";
import { SITES, SITE_SHORT } from "@/lib/sites";
import { Switch } from "@/components/ui/switch";
import BulkImportDialog from "@/components/BulkImportDialog";

function SitePicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const val = value || [];
  const toggle = (code) => {
    const next = val.includes(code) ? val.filter((c) => c !== code) : [...val, code];
    onChange(next.length ? next : ["District"]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-56 h-9 px-3 rounded-md border border-zinc-200 bg-white text-zinc-950 text-left text-xs flex items-center justify-between gap-2 hover:border-zinc-400 dark:border-zinc-700 dark:text-white transition-colors duration-200"
        >
          <span className="truncate">
            {val.length === 0 && <span className="text-zinc-400">Select sites…</span>}
            {val.length === 1 && (SITE_SHORT[val[0]] || val[0])}
            {val.length > 1 && <span className="font-mono">{val.map((c) => SITE_SHORT[c] || c).join(" · ")}</span>}
          </span>
          <ChevronDown className="h-3 w-3 text-zinc-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2 bg-white dark:bg-zinc-900 dark:border-zinc-700">
        <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 px-2 py-1.5">Assign sites</div>
        <ul className="space-y-0.5">
          {SITES.map((s) => (
            <li key={s.code}>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50 cursor-pointer text-sm">
                <Checkbox
                  checked={val.includes(s.code)}
                  onCheckedChange={() => toggle(s.code)}
                  data-testid={`site-check-${s.code}`}
                />
                <span className="text-zinc-800">{s.label}</span>
                <span className="ml-auto text-[10px] font-mono text-zinc-400">{s.short}</span>
              </label>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export default function AdminUsers() {
  const { user: me, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    try { setUsers((await api.get("/users")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, payload) => {
    try { await api.patch(`/users/${id}`, payload); await load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const viewAs = async (u) => {
    try {
      const impUser = await impersonate(u.id);
      toast.success(`Now viewing as ${impUser.name}`);
      // Land on dashboard so the master admin sees exactly what the impersonated user sees
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Administration · ESUSD</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">Users, Roles & Sites</h1>
          <p className="text-sm text-zinc-500 mt-1">Assign school sites (multi-select) and a role to control what each user sees.</p>
        </div>
        <Button
          onClick={() => setBulkOpen(true)}
          data-testid="open-bulk-import-button"
          className="bg-zinc-950 hover:bg-zinc-800 text-white h-11 rounded-md transition-colors duration-200"
        >
          <Upload className="h-4 w-4 mr-2" /> Bulk import CSV
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md mt-6 overflow-x-auto floating-x-scroll">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Sites</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Department</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold text-center">Email alerts</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-b border-zinc-100" data-testid={`user-row-${u.id}`}>
                <TableCell className="text-sm text-zinc-950 dark:text-white">{u.name}</TableCell>
                <TableCell className="text-sm text-zinc-700">{u.email}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}>
                    <SelectTrigger className="w-32 h-9" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">user</SelectItem>
                      <SelectItem value="teacher">teacher</SelectItem>
                      <SelectItem value="secretary">secretary</SelectItem>
                      <SelectItem value="principal">principal</SelectItem>
                      <SelectItem value="it">it</SelectItem>
                      <SelectItem value="maintenance">maintenance</SelectItem>
                      <SelectItem value="custodian">custodian</SelectItem>
                      <SelectItem value="mechanic">mechanic</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <SitePicker
                    value={u.sites || (u.site ? [u.site] : ["District"])}
                    onChange={(next) => patch(u.id, { sites: next })}
                    testId={`sites-picker-${u.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={u.department || "none"}
                    onValueChange={(v) => patch(u.id, { department: v === "none" ? null : v })}
                  >
                    <SelectTrigger className="w-40 h-9" data-testid={`dept-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Custodial">Custodial</SelectItem>
                      <SelectItem value="Mechanic">Mechanic</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-2" title={u.email_notifications === true ? "Emails on" : "Emails muted (default off)"}>
                    <Switch
                      checked={u.email_notifications === true}
                      onCheckedChange={(v) => patch(u.id, { email_notifications: !!v })}
                      data-testid={`email-toggle-${u.id}`}
                      className="data-[state=unchecked]:bg-red-200 dark:data-[state=unchecked]:bg-red-950/60"
                    />
                    {u.email_notifications === true
                      ? <Bell className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.2} />
                      : <BellOff className="h-3.5 w-3.5 text-red-500" strokeWidth={2.2} />}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.id !== me?.id && u.role !== "admin" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewAs(u)}
                        data-testid={`view-as-${u.id}`}
                        title="View as this user"
                        className="text-zinc-600 hover:text-[#0055FF] hover:bg-zinc-100"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`user-delete-${u.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={load} />
    </div>
  );
}
